// src/stores/notification.store.ts
import { create } from 'zustand';
import type { NotificationItem, NotificationPagination } from '../models/notification.model.ts';

interface NotificationState {
  notifications: NotificationItem[];
  pagination: NotificationPagination | null;
  hasUnread: boolean;
  loading: boolean;
  nextLoading: boolean;
  error: string | null;

  setList: (notifications: NotificationItem[], pagination: NotificationPagination) => void;
  appendList: (notifications: NotificationItem[], pagination: NotificationPagination) => void;
  setHasUnread: (hasUnread: boolean) => void;
  setLoading: (loading: boolean) => void;
  setNextLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  removeItem: (id: number) => void;
  markItemRead: (id: number) => void;
  markAllRead: () => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  pagination: null,
  hasUnread: false,
  loading: false,
  nextLoading: false,
  error: null,

  setList: (notifications, pagination) =>
    set({ notifications, pagination }),

  appendList: (notifications, pagination) =>
    set((s) => ({ notifications: [...s.notifications, ...notifications], pagination })),

  setHasUnread: (hasUnread) => set({ hasUnread }),

  setLoading: (loading) => set({ loading }),

  setNextLoading: (nextLoading) => set({ nextLoading }),

  setError: (error) => set({ error }),

  removeItem: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),

  markItemRead: (id) =>
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, is_read: true } : n
      ),
    })),

  markAllRead: () =>
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, is_read: true })),
      hasUnread: false,
    })),

  clearAll: () =>
    set({ notifications: [], pagination: null, hasUnread: false, loading: false, nextLoading: false, error: null }),
}));
